import React from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import { isOptionEqualToValue } from 'feature/dispatching/utils';
import { formatGallons } from 'utils/helpers/format';
import LoadingTextField from './LoadingTextField';
import HouseRecipeForm from './HouseRecipeForm';
import CustomRecipeForm from './CustomRecipeForm';
import UnloadForm from './UnloadForm';
import {
  useGetLoadTypesQuery,
  useLazyGetCustomRecipeAllocationQuery,
  useLazyGetHouseRecipeAllocationQuery,
  useLazyGetUnloadAllocationQuery,
} from '../../loadsEndpoints';
import { initialCompartmentState } from './initialState';
import VolumeInput from './VolumeInput';

const showV2Features = process.env.REACT_APP_DISPATCHING_VERSION >= '2.0.0';

const Compartment = ({
  loadState,
  setLoadState,
  compartment,
  validationState,
  setValidationState,
}) => {
  const { fillAll, location, driver } = loadState;
  const { capacityInGallons, compartmentId, loadType, supplier, recipe, products, product } =
    compartment;
  const isCustom = R.propEq('loadTypeId', 1, loadType);
  const isHouse = R.propEq('loadTypeId', 2, loadType);
  const isUnload = R.propEq('loadTypeId', 3, loadType);
  const containerType = fillAll ? 'trailer' : 'compartment';

  const initialValidationState = {
    error: false,
    isFetching: false,
    message: `The max volume for this ${containerType} is ${formatGallons(capacityInGallons)}`,
  };

  const loadTypeFilter = ({ type }) => {
    const loadTypes = R.compose(
      R.map(R.path(['loadType', 'type'])),
      R.values
    )(loadState.compartments);
    if (!showV2Features && type === 'Unload') return false;
    if (loadTypes.includes('Load') && type === 'Unload') return false;
    if (loadTypes.includes('Unload') && type === 'Load') return false;
    return type === 'Load'
      ? ['LOAD & UNLOAD', 'LOAD ONLY'].includes(driver.loadCapabilities)
      : ['LOAD & UNLOAD', 'UNLOAD ONLY'].includes(driver.loadCapabilities);
  };

  const {
    data: loadTypesQuery = [],
    isFetching: isFetchingLoadTypes,
    isSuccess: isLoadTypesSuccess,
  } = useGetLoadTypesQuery(
    { locationId: R.prop('locationId', location) },
    {
      selectFromResult: result => ({
        ...result,
        data: result.data?.filter(loadTypeFilter),
      }),
    }
  );

  const trailerLoad = R.filter(R.eqProps('type', { type: 'Load' }), loadTypesQuery);
  const volumeDisabled = R.isNil(compartment?.recipe);
  const handleLoadTypeChange = (_, value) => {
    const c = R.objOf(compartmentId, {
      ...initialCompartmentState,
      loadType: value,
    });
    setLoadState(R.mergeDeepLeft({ compartments: c }));
    setValidationState(initialValidationState);
  };

  const [triggerGetCustomRecipeAllocation] = useLazyGetCustomRecipeAllocationQuery();
  const [triggerGetHouseRecipeAllocation] = useLazyGetHouseRecipeAllocationQuery();
  const [triggerGetUnloadAllocationQuery] = useLazyGetUnloadAllocationQuery();

  const validateVolume = value => {
    const locationId = R.prop('locationId', location);
    const recipeId = R.prop('recipeId', recipe);
    const supplierId = R.prop('supplierId', supplier);
    const productId = R.prop('productId', product);
    const productGradeCode = R.prop('productGradeCode', recipe);

    const getAllocation = isCustom
      ? triggerGetCustomRecipeAllocation
      : isHouse
      ? triggerGetHouseRecipeAllocation
      : triggerGetUnloadAllocationQuery; // Unload
    const args = isCustom
      ? { locationId, productGradeCode, supplierId }
      : isHouse
      ? {
          locationId,
          productGradeCode,
          products: R.map(
            p => ({ productGradeCode: p.productGradeCode, supplierId: p.supplier.supplierId }),
            R.values(products)
          ),
          recipeId,
        }
      : { locationId, productId, supplierId }; // Unload

    setValidationState(
      R.mergeRight(validationState, { isFetching: true, message: 'Checking allocation...' })
    );

    getAllocation(args, true)
      .unwrap()
      .then(({ allocation }) => {
        if (R.gt(value, allocation))
          return `The max allocation for this supplier is ${allocation} gallons`;
        if (R.isEmpty(value)) return 'Volume required';
        if (R.lt(value, 1)) return 'Volume must be greater than 0';
        if (R.gt(value, capacityInGallons))
          return `The max volume for this compartment is ${formatGallons(capacityInGallons)}`;
        return '';
      })
      .then(message => {
        if (R.isEmpty(message)) setValidationState(initialValidationState);
        else setValidationState({ error: true, isFetching: false, message });
      });
  };

  return (
    <Stack spacing={2}>
      <Autocomplete
        disabled={!isLoadTypesSuccess}
        getOptionLabel={R.prop('name')}
        isOptionEqualToValue={isOptionEqualToValue('loadTypeId')}
        loading={isFetchingLoadTypes}
        onChange={handleLoadTypeChange}
        options={containerType === 'trailer' ? trailerLoad : loadTypesQuery}
        renderInput={params => (
          <LoadingTextField label="Load Type" loading={isFetchingLoadTypes} params={params} />
        )}
        value={loadType}
      />

      {isCustom && (
        <CustomRecipeForm
          compartment={compartment}
          loadState={loadState}
          setLoadState={setLoadState}
          setValidationState={() => {
            setValidationState(initialValidationState);
          }}
        />
      )}

      {isHouse && (
        <HouseRecipeForm
          compartment={compartment}
          loadState={loadState}
          setLoadState={setLoadState}
          setValidationState={() => {
            setValidationState(initialValidationState);
          }}
        />
      )}

      {isUnload && (
        <UnloadForm compartment={compartment} loadState={loadState} setLoadState={setLoadState} />
      )}
      {isCustom || isHouse ? (
        <VolumeInput
          compartment={compartment}
          disabled={volumeDisabled}
          error={validationState.error}
          helperText={validationState.message}
          label="Volume"
          loadState={loadState}
          setLoadState={setLoadState}
          validateVolume={validateVolume}
          validationState={validationState}
          value={compartment.volume}
        />
      ) : null}
    </Stack>
  );
};

Compartment.propTypes = {
  compartment: PropTypes.shape({
    capacityInGallons: PropTypes.number,
    compartmentId: PropTypes.number,
    loadType: PropTypes.shape({}),
    product: PropTypes.shape({}),
    products: PropTypes.shape({}),
    recipe: PropTypes.shape({}),
    supplier: PropTypes.shape({}),
    volume: PropTypes.oneOfType([PropTypes.string]),
  }).isRequired,
  loadState: PropTypes.shape({
    compartments: PropTypes.shape({}),
    driver: PropTypes.shape({
      loadCapabilities: PropTypes.string,
    }),
    fillAll: PropTypes.bool,
    location: PropTypes.shape({}),
  }).isRequired,
  setLoadState: PropTypes.func.isRequired,
  setValidationState: PropTypes.func.isRequired,
  validationState: PropTypes.shape({
    error: PropTypes.bool,
    message: PropTypes.string,
  }).isRequired,
};

export default Compartment;
